import { ApolloClient, gql, makeVar } from '@apollo/client'

import { impersonatorTokenKey } from 'src/utils/impersonator'

/**
 * Whether the REAL WasteHero-staff user behind an impersonation session is
 * staff. Distinct from the `wastehero-staff` role (which reflects the CURRENTLY
 * authenticated — i.e. impersonated — user) and from `impersonator` (which only
 * means "someone is impersonating"). `setRoles` reads this to grant the
 * `staff-impersonator` role so staff-only tools (the feature-config admin) stay
 * reachable while impersonating a tenant.
 *
 * gql-only leaf (PRO-130): no UI/hook imports, safe to load in the boot batch.
 */
export const impersonatorIsStaffVar = makeVar<boolean>(false)

const IMPERSONATOR_ME = gql`
  query ImpersonatorMe {
    me {
      id
      isStaff
    }
  }
`

/**
 * When an impersonation session is active, ask the backend `me { isStaff }`
 * using the retained impersonator (real staff) token and record whether that
 * real user is staff. Fired in the boot batch; must resolve before `setUser`
 * runs `setRoles`, so the role is present on the first roles computation (a
 * page reload always re-runs boot after impersonation starts).
 *
 * Never throws: any failure leaves the flag OFF (fail-safe — no staff surface).
 */
export async function loadImpersonatorMe(
  client: ApolloClient<object>
): Promise<void> {
  // impersonatorTokenKey is only set while impersonating; when absent the
  // useImpersonatorTokenIfExist path would fall back to the current JWT, so we
  // must not query — the current user is not necessarily the impersonator.
  if (!localStorage.getItem(impersonatorTokenKey)) {
    impersonatorIsStaffVar(false)
    return
  }
  try {
    const { data } = await client.query({
      query: IMPERSONATOR_ME,
      context: { useImpersonatorTokenIfExist: true },
      fetchPolicy: 'no-cache',
    })
    impersonatorIsStaffVar(Boolean(data?.me?.isStaff))
  } catch {
    impersonatorIsStaffVar(false)
  }
}
